import * as React from "react";
import styled, { css, useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { Box, Group, Progress, Skeleton, Stack, Text, Title, Tooltip } from "components/common";
import useDataLakes from "data-lake/hooks/useDataLakes";
import {
  useDataLakeIcebergAggregateMetrics,
  useDataLakeIcebergStreamMetrics
} from "data-lake/hooks/useDataLakeIcebergMetrics";
import NumberUtils from "util/NumberUtils";
import { formatNumber } from "util/NumberFormatting";
import { defaultChartColors } from "views/components/visualizations/Colors";
const CHART_PAGE_SIZE = 1e3;
const MAX_VISIBLE_STREAMS = 7;
const MINIMUM_LABEL_PERCENTAGE = 8;
const searchParams = {
  page: 1,
  pageSize: CHART_PAGE_SIZE,
  query: "",
  sort: { attributeId: "stream_id", direction: "asc" }
};
const Container = styled.div(
  ({ theme }) => css`
    margin-bottom: ${theme.spacings.xxs};
  `
);
const MutedText = styled(Text)(
  ({ theme }) => css`
    color: ${theme.colors.gray[60]};
  `
);
const SummaryTitle = styled(Title)`
  margin: 0;
`;
const StyledProgressRoot = styled(Progress.Root)(
  ({ theme }) => css`
    background-color: ${theme.colors.gray[90]};
    overflow: hidden;
  `
);
const StyledProgressSection = styled(Progress.Section)(
  ({ $withMinimumWidth }) => css`
    ${$withMinimumWidth && css`
      min-width: 4px;
    `}
  `
);
const SegmentLabel = styled(Progress.Label)(
  ({ $backgroundColor, theme }) => css`
    color: ${theme.utils.readableColor($backgroundColor)};
    font-weight: bold;
    white-space: nowrap;
  `
);
const LegendSwatch = styled(Box)(
  ({ $backgroundColor }) => css`
    width: 10px;
    height: 10px;
    border-radius: 2px;
    background-color: ${$backgroundColor};
    flex: 0 0 auto;
  `
);
const EmptyBar = styled(StyledProgressRoot)(
  ({ theme }) => css`
    border: 1px solid ${theme.colors.gray[80]};
  `
);
const roundPercentage = (percentage) => Math.round(percentage * 10) / 10;
const formatPercentage = (percentage) => `${formatNumber(percentage, { digits: 1, minimumDigits: 1 })}%`;
const buildStreamSegments = (dataLakes, metricsByStreamId) => dataLakes.map(({ stream_id: streamId, stream_title: title }) => {
  var _a, _b, _c;
  const metrics = metricsByStreamId.get(streamId);
  return {
    id: streamId,
    name: title != null ? title : i18n.t("labels.deleted_stream", { ns: "enterprise_data_lake" }),
    bytes: (_a = metrics == null ? void 0 : metrics.bytes) != null ? _a : 0,
    records: (_b = metrics == null ? void 0 : metrics.records) != null ? _b : 0,
    files: (_c = metrics == null ? void 0 : metrics.files) != null ? _c : 0
  };
}).filter(({ bytes }) => bytes > 0).sort((left, right) => right.bytes - left.bytes || left.name.localeCompare(right.name));
const sumBytes = (segments) => segments.reduce((sum, { bytes }) => sum + bytes, 0);
const sumRecords = (segments) => segments.reduce((sum, { records }) => sum + records, 0);
const sumFiles = (segments) => segments.reduce((sum, { files }) => sum + files, 0);
const getSegmentColor = (index) => defaultChartColors[index % defaultChartColors.length];
const buildVisibleSegments = (segments, otherColor) => {
  if (segments.length <= MAX_VISIBLE_STREAMS) {
    return segments.map((segment, index) => ({
      ...segment,
      color: getSegmentColor(index)
    }));
  }
  const topSegments = segments.slice(0, MAX_VISIBLE_STREAMS).map((segment, index) => ({
    ...segment,
    color: getSegmentColor(index)
  }));
  const otherSegments = segments.slice(MAX_VISIBLE_STREAMS);
  return [
    ...topSegments,
    {
      id: "other",
      name: i18n.t("retrievable_chart.other_streams", { ns: "enterprise_data_lake", count: otherSegments.length }),
      bytes: sumBytes(otherSegments),
      records: sumRecords(otherSegments),
      files: sumFiles(otherSegments),
      color: otherColor
    }
  ];
};
const addPercentages = (segments, totalBytes) => {
  if (segments.length === 0 || totalBytes <= 0) {
    return [];
  }
  const roundedSegments = segments.map((segment) => ({
    ...segment,
    percentage: roundPercentage(segment.bytes / totalBytes * 100),
    isOther: segment.id === "other"
  }));
  const largestSegment = roundedSegments.reduce(
    (largest, segment, index) => segment.bytes > roundedSegments[largest].bytes ? index : largest,
    0
  );
  const percentageSum = roundedSegments.reduce((sum, { percentage }) => sum + percentage, 0);
  const correction = roundPercentage(100 - percentageSum);
  return roundedSegments.map(
    (segment, index) => index === largestSegment ? { ...segment, percentage: roundPercentage(segment.percentage + correction) } : segment
  );
};
const LoadingState = () => /* @__PURE__ */ React.createElement(Container, { "data-testid": "data-lake-retrievable-size-skeleton" }, /* @__PURE__ */ React.createElement(Stack, { gap: "sm" }, /* @__PURE__ */ React.createElement(Skeleton, { height: 34, width: 260 }), /* @__PURE__ */ React.createElement(Skeleton, { height: 30 }), /* @__PURE__ */ React.createElement(Group, { gap: "md" }, ["first", "second", "third"].map((key) => /* @__PURE__ */ React.createElement(Skeleton, { key, height: 16, width: 130 })))));
const Header = ({
  totalBytes,
  totalFiles,
  totalRecords,
  totalStreams
}) => {
  const { t } = useTranslation("enterprise_data_lake");
  const formatCount = (value, unit) => t(`retrievable_chart.count_${unit}`, { count: value, formattedCount: formatNumber(value) });
  return /* @__PURE__ */ React.createElement(Group, { align: "flex-start", justify: "space-between" }, /* @__PURE__ */ React.createElement(Stack, { gap: 4 }, /* @__PURE__ */ React.createElement(Title, { order: 2 }, t("retrievable_chart.title")), /* @__PURE__ */ React.createElement(Group, { align: "baseline", gap: "xs" }, /* @__PURE__ */ React.createElement(SummaryTitle, { order: 3 }, NumberUtils.formatDecimalBytes(totalBytes)), /* @__PURE__ */ React.createElement(MutedText, { size: "sm" }, t("retrievable_chart.across", { streams: formatCount(totalStreams, "stream") })))), /* @__PURE__ */ React.createElement(Stack, { align: "flex-end", gap: 2 }, /* @__PURE__ */ React.createElement(MutedText, { size: "sm" }, formatCount(totalRecords, "record")), /* @__PURE__ */ React.createElement(MutedText, { size: "sm" }, formatCount(totalFiles, "file"))));
};
const EmptyState = ({
  totalFiles,
  totalRecords,
  totalStreams
}) => {
  const { t } = useTranslation("enterprise_data_lake");
  return /* @__PURE__ */ React.createElement(Container, null, totalStreams === 0 ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Title, { order: 2 }, t("retrievable_chart.title")), /* @__PURE__ */ React.createElement(MutedText, { size: "sm" }, t("retrievable_chart.no_streams"))) : /* @__PURE__ */ React.createElement(Stack, { gap: "sm" }, /* @__PURE__ */ React.createElement(Header, { totalBytes: 0, totalFiles, totalRecords, totalStreams }), /* @__PURE__ */ React.createElement(EmptyBar, { "data-testid": "data-lake-retrievable-size-bar", size: 30, radius: "sm" }), /* @__PURE__ */ React.createElement(MutedText, { size: "sm" }, t("retrievable_chart.no_data"))));
};
const DataLakeRetrievableSizeChart = () => {
  const theme = useTheme();
  const {
    data: { list: dataLakes },
    isInitialLoading: isLoadingDataLakes
  } = useDataLakes(searchParams);
  const aggregateMetrics = useDataLakeIcebergAggregateMetrics();
  const streamIds = dataLakes.map(({ stream_id: streamId }) => streamId);
  const { metricsByStreamId, isLoading: isLoadingStreamMetrics } = useDataLakeIcebergStreamMetrics(streamIds);
  if (isLoadingDataLakes || aggregateMetrics.isLoading || isLoadingStreamMetrics) {
    return /* @__PURE__ */ React.createElement(LoadingState, null);
  }
  const streamSegments = buildStreamSegments(dataLakes, metricsByStreamId);
  const segmentTotalBytes = sumBytes(streamSegments);
  const totalBytes = aggregateMetrics.totalBytes > 0 ? aggregateMetrics.totalBytes : segmentTotalBytes;
  const totalRecords = aggregateMetrics.totalRecords > 0 ? aggregateMetrics.totalRecords : sumRecords(streamSegments);
  const totalFiles = aggregateMetrics.totalDataFiles > 0 ? aggregateMetrics.totalDataFiles : sumFiles(streamSegments);
  const totalStreams = dataLakes.length;
  if (totalStreams === 0 || totalBytes <= 0 || streamSegments.length === 0) {
    return /* @__PURE__ */ React.createElement(EmptyState, { totalFiles, totalRecords, totalStreams });
  }
  const visibleSegments = addPercentages(buildVisibleSegments(streamSegments, theme.colors.gray[60]), totalBytes);
  return /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(Stack, { gap: "sm" }, /* @__PURE__ */ React.createElement(
    Header,
    {
      totalBytes,
      totalFiles,
      totalRecords,
      totalStreams
    }
  ), /* @__PURE__ */ React.createElement(StyledProgressRoot, { "data-testid": "data-lake-retrievable-size-bar", size: 30, radius: "sm" }, visibleSegments.map((segment) => {
    const formattedPercentage = formatPercentage(segment.percentage);
    const tooltipLabel = `${segment.name} \xB7 ${NumberUtils.formatDecimalBytes(segment.bytes)} \xB7 ${formattedPercentage}`;
    return /* @__PURE__ */ React.createElement(Tooltip, { key: segment.id, label: tooltipLabel, withArrow: true }, /* @__PURE__ */ React.createElement(
      StyledProgressSection,
      {
        $withMinimumWidth: segment.percentage > 0 && segment.percentage < 1,
        "aria-label": tooltipLabel,
        color: segment.color,
        "data-testid": `data-lake-retrievable-size-segment-${segment.id}`,
        value: segment.percentage
      },
      segment.percentage >= MINIMUM_LABEL_PERCENTAGE && /* @__PURE__ */ React.createElement(SegmentLabel, { $backgroundColor: segment.color }, NumberUtils.formatDecimalBytes(segment.bytes))
    ));
  })), /* @__PURE__ */ React.createElement(Group, { gap: "md", wrap: "wrap" }, visibleSegments.map(({ color, id, name, percentage }) => /* @__PURE__ */ React.createElement(Group, { key: id, gap: "xs", wrap: "nowrap" }, /* @__PURE__ */ React.createElement(LegendSwatch, { $backgroundColor: color }), /* @__PURE__ */ React.createElement(Text, { size: "sm" }, name), /* @__PURE__ */ React.createElement(MutedText, { size: "sm" }, `${formatPercentage(percentage)}`))))));
};
export default DataLakeRetrievableSizeChart;
